/**
 * The lobby/presence/live-match socket.
 *
 * A thin wrapper, not a game-rules layer: it only knows how to stay
 * connected and dispatch typed messages. `main.js` subscribes to the
 * presence/lobby/invite events for the portal UI, and `Session` (via
 * main.js) subscribes to `move`/`opponent_left` for a 'remote' game — see
 * the plan this shipped under for why the wire protocol looks the way it
 * does (api/server.js's WebSocket layer).
 *
 * Mirrors `apiProvider.js`'s `BASE`/token conventions so the two stay
 * obviously related rather than inventing a second way to find either.
 */

const BASE = import.meta.env.VITE_API_URL;
const SESSION_KEY = 'battlechess.session';

function currentToken() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY))?.token ?? null;
  } catch {
    return null;
  }
}

const wsUrl = (token) => `${BASE.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;

const MAX_BACKOFF = 15000;

class Realtime {
  constructor() {
    this.socket = null;
    this.wanted = false;      // true between connect() and disconnect()
    this.backoff = 1000;
    this.retryTimer = null;
    this.handlers = new Map(); // type -> Set<fn>
  }

  /** Opens the socket; safe to call repeatedly (a no-op while already open
   * or trying). Does nothing without a configured backend or a token — the
   * caller (main.js) is expected to gate this on "signed in, not a guest". */
  connect() {
    this.wanted = true;
    if (!BASE) return;
    const token = currentToken();
    if (!token) return;
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    let socket;
    try {
      socket = new WebSocket(wsUrl(token));
    } catch {
      // Construction itself can throw synchronously (e.g. a CSP connect-src
      // block raises SecurityError here, not via onerror) — left uncaught,
      // this would propagate out through auth.onChange's listener loop and
      // abort the rest of main.js's top-level script. Treat it exactly like
      // a dropped connection instead: back off and retry.
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 1.7, MAX_BACKOFF);
      return;
    }
    this.socket = socket;

    socket.onopen = () => { this.backoff = 1000; };
    socket.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      for (const fn of this.handlers.get(msg.type) ?? []) fn(msg);
    };
    socket.onclose = () => {
      if (this.socket !== socket) return; // superseded by a newer connect()
      this.socket = null;
      if (!this.wanted) return;
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 1.7, MAX_BACKOFF);
    };
    socket.onerror = () => { /* onclose follows every onerror; handled there */ };
  }

  /** Closes the socket and stops reconnecting — call on sign-out. */
  disconnect() {
    this.wanted = false;
    clearTimeout(this.retryTimer);
    this.socket?.close();
    this.socket = null;
  }

  send(type, payload = {}) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type, ...payload }));
    }
  }

  /** Subscribes to one message type; returns an unsubscribe function. */
  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }
}

export const realtime = new Realtime();
